/**
 * Utility helpers for the Parent Report page.
 * Pure functions — no DOM/side effects.
 */

// Map backend priority keys -> human pill labels.
// KEYS MUST MATCH the questionnaire's q17_priorities options verbatim.
// Source of truth: /app/frontend/src/data/questionnaireData.js (q17_priorities).
export const PRIORITY_LABELS = {
  strong_academic_rigour: 'Strong academic rigour',
  child_feels_known_supported: 'A school where my child feels known and supported',
  excellent_teachers_stay: 'Excellent teachers who stay',
  clear_university_placement: 'Clear university placement record',
  diverse_international_community: 'Diverse, international student community',
  curriculum_for_world_ahead: 'A curriculum built for the world ahead',
  strong_cocurricular: 'Strong co-curricular life — competitions, arts, sports, debate',
  excellent_english_programme: 'Excellent English programme',
  mandarin_programme: 'Mandarin programme',
  reasonable_fees_value: 'Reasonable fees and good value',
  close_to_home: 'Close to home',
  // Legacy / internal synonyms — keep for back-compat with older reports
  university_pathways: 'Clear university placement record',
  co_curricular_opportunities: 'Strong co-curricular life — competitions, arts, sports, debate',
  future_focused_programmes: 'A curriculum built for the world ahead',
  mother_tongue_support: 'Mandarin programme',
  diverse_community: 'Diverse, international student community',
  caring_pastoral_team: 'A school where my child feels known and supported',
  arts_creativity: 'Strong co-curricular life — competitions, arts, sports, debate',
  stem_excellence: 'A curriculum built for the world ahead',
};

// Detailed labels — same verbatim text as above (kept as a separate map so we can
// deviate later if we want snappier copy on pills vs a list).
export const PRIORITY_LABELS_DETAILED = { ...PRIORITY_LABELS };

export const toPriorityLabel = (key) => {
  if (!key) return null;
  return PRIORITY_LABELS[key] || key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

export const toPriorityLabelDetailed = (key) => {
  if (!key) return null;
  return PRIORITY_LABELS_DETAILED[key] || toPriorityLabel(key);
};

// Budget band -> human label
export const BUDGET_LABELS = {
  under_30k: 'Under RM 30,000',
  '30_50k': 'RM 30,000–RM 50,000',
  '50_80k': 'RM 50,000–RM 80,000',
  above_80k: 'Above RM 80,000',
  flexible: 'Flexible budget',
};
export const toBudgetLabel = (b) => BUDGET_LABELS[b] || b;

// Pill-friendly, compact fee-range label (e.g. "RM 50k–80k")
export const BUDGET_PILL_LABELS = {
  under_30k: 'Annual fees under RM 30k',
  '30_50k': 'Annual fees RM 30k–50k',
  '50_80k': 'Annual fees RM 50k–80k',
  above_80k: 'Annual fees above RM 80k',
  flexible: 'Flexible on fees',
};
export const toBudgetPill = (b) => BUDGET_PILL_LABELS[b] || (b ? `Annual fees ${b}` : null);

// Location key -> human label. Mirrors q21_location options in questionnaireData.js.
export const LOCATION_LABELS = {
  mont_kiara:   'Mont Kiara / Hartamas',
  kl_city:      'KL City Centre',
  ampang:       'Ampang / KLCC East',
  cheras:       'Cheras',
  petaling_jaya:'Petaling Jaya / Damansara',
  subang:       'Subang Jaya / USJ',
  puchong:      'Puchong',
  sungai_buloh: 'Sungai Buloh / Elmina',
  shah_alam:    'Shah Alam / Setia Alam',
  klang:        'Klang / Port Klang',
  kajang_bangi: 'Kajang / Bangi',
  putrajaya:    'Putrajaya / Cyberjaya',
  rawang:       'Rawang / Kundang',
  flexible:     'Flexible',
  // Backend internals also seen on reports
  kl_central:   'KL City Centre',
  damansara:    'Petaling Jaya / Damansara',
};
export const toLocationLabel = (k) => {
  if (!k) return null;
  if (LOCATION_LABELS[k]) return LOCATION_LABELS[k];
  // Fallback: clean up snake_case → Title Case (e.g. "sri_hartamas" → "Sri Hartamas")
  return String(k).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

// Friendly zone display names (for match-reasoning text and blurred teasers).
// Keyed by the backend's zone slugs (see enhanced_matching_algorithm LOCATION_ZONES).
export const ZONE_DISPLAY = {
  mont_kiara:   'Mont Kiara / Hartamas',
  kl_central:   'KL City Centre',
  kl_city:      'KL City Centre',
  ampang:       'Ampang / KLCC East',
  cheras:       'Cheras',
  petaling_jaya:'Petaling Jaya / Damansara',
  subang:       'Subang Jaya / USJ',
  puchong:      'Puchong',
  sungai_buloh: 'Sungai Buloh / Elmina',
  shah_alam:    'Shah Alam / Setia Alam',
  klang:        'Klang / Port Klang',
  kajang_bangi: 'Kajang / Bangi',
  putrajaya:    'Putrajaya / Cyberjaya',
  rawang:       'Rawang / Kundang',
  flexible:     'Flexible',
  damansara:    'Petaling Jaya / Damansara',
};
export const prettyZone = (slug) => {
  if (!slug) return null;
  if (ZONE_DISPLAY[slug]) return ZONE_DISPLAY[slug];
  return String(slug).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

// Detect which zone a campus_locations string belongs to. Used to honestly
// classify a school's area without exposing the street-level address on
// blurred cards. Keywords mirror backend LOCATION_ZONES.
const ZONE_KEYWORDS = {
  mont_kiara:   ['mont kiara', 'desa parkcity', 'hartamas', 'sri hartamas', 'dutamas'],
  petaling_jaya:['petaling jaya', 'pj', 'damansara', 'kota damansara', 'damansara heights',
                 'kelana jaya', 'ttdi', 'taman tun', 'bandar utama', 'tropicana'],
  subang:       ['subang jaya', 'subang', 'usj', 'sunway', 'ara damansara'],
  puchong:      ['puchong', 'bandar puteri', 'bukit puchong'],
  cheras:       ['cheras'],
  ampang:       ['ampang', 'setiawangsa', 'wangsa maju', 'ampang hilir', 'ampang jaya', 'jalan ampang'],
  kl_central:   ['bangsar', 'brickfields', 'kl sentral', 'kl central', 'kuala lumpur', 'kl city',
                 'old klang road', 'seputeh', 'jalan klang lama', 'pudu', 'jalan bellamy', 'bukit petaling'],
  putrajaya:    ['putrajaya', 'cyberjaya', 'sepang'],
  klang:        ['klang', 'port klang', 'meru'],
  shah_alam:    ['shah alam', 'setia alam', 'setia eco park', 'setia ecohill', 'semenyih',
                 'bandar rimbayu', 'bukit jelutong', 'putra heights'],
  kajang_bangi: ['kajang', 'bangi', 'cheras perdana', 'balakong', 'jade hills',
                 'hulu langat', 'mines', 'seri kembangan'],
  rawang:       ['rawang', 'kundang'],
  sungai_buloh: ['sungai buloh', 'sierramas', 'valencia', 'elmina', 'gombak', 'batu caves'],
};
// Order matters: more-specific zones before kl_central (which would otherwise
// catch bare "kuala lumpur" in addresses like "Jalan Ampang, Kuala Lumpur").
const ZONE_ORDER = [
  'mont_kiara', 'petaling_jaya', 'subang', 'puchong', 'cheras', 'ampang',
  'shah_alam', 'kajang_bangi', 'rawang', 'sungai_buloh', 'putrajaya',
  'klang', 'kl_central',
];

export const campusZone = (campusLocation) => {
  if (!campusLocation) return null;
  const lower = String(campusLocation).toLowerCase().replace(/_/g, ' ');
  for (const zone of ZONE_ORDER) {
    if (ZONE_KEYWORDS[zone].some((kw) => lower.includes(kw))) return zone;
  }
  return null;
};

// Friendly location for a school. Prefers the zone-level label for revealed
// cards ("Mont Kiara", "Ampang") rather than the raw campus string, and never
// leaks a specific address on blurred cards.
export const friendlySchoolLocation = (school) => {
  const raw = Array.isArray(school?.campus_locations) && school.campus_locations.length
    ? school.campus_locations[0]
    : null;
  if (!raw) return null;
  const zone = campusZone(raw);
  return zone ? prettyZone(zone) : raw;
};

// Replace any raw zone slug that leaked through from the backend (e.g.
// "In your preferred mont_kiara area") with its friendly display name.
export const prettifyReason = (reason, summary) => {
  if (!reason) return reason;
  let out = String(reason);
  // Replace every known slug that appears verbatim
  Object.keys(ZONE_DISPLAY).forEach((slug) => {
    const re = new RegExp(`\\b${slug}\\b`, 'gi');
    out = out.replace(re, ZONE_DISPLAY[slug]);
  });
  // Pretty-print the parent's preference if embedded raw
  const pref = summary?.location_preference;
  if (pref) {
    const re = new RegExp(`\\b${pref}\\b`, 'gi');
    out = out.replace(re, prettyZone(pref));
  }
  return out;
};

// Canonical display name for a school — handles the Mont'Kiara naming
// ambiguity explicitly (MKIS vs the location).
export const displayName = (school) => {
  if (!school) return '';
  const full = school.full_name || '';
  if (full === "Mont'Kiara International School") return "Mont'Kiara International School";
  // Compact chip/label version — used sparingly in paragraphs
  return school.full_name || school.common_name || school.school_name || '';
};

// A shorter display form for running copy ("MKIS" rather than the full name)
export const shortDisplayName = (school) => {
  if (!school) return '';
  const full = school.full_name || '';
  if (full === "Mont'Kiara International School") return 'MKIS';
  // Prefer common_name only if it's long enough to be unambiguous
  const cn = school.common_name || '';
  if (cn && cn.length >= 4) return cn;
  return full || cn;
};

// Determine whether a school sits outside the parent's preferred area.
// Returns { outside: bool, adjacent: bool, zone: string|null, preferredZone: string|null }
export const locationFitVsPreference = (school, summary) => {
  const rawPreferred = summary?.location_preference;
  const pref = rawPreferred ? String(rawPreferred).toLowerCase().trim() : '';
  const schoolZone = campusZone(
    Array.isArray(school?.campus_locations) ? school.campus_locations[0] : null
  );
  // Any variation of "flexible" means all zones are acceptable — never surface
  // an "outside your preferred area" string in that case.
  const isFlexible = !pref
    || pref === 'flexible'
    || pref === 'flexible — depends on the school'
    || pref.startsWith('flexible');
  if (isFlexible) {
    return { outside: false, adjacent: false, zone: schoolZone, preferredZone: null };
  }
  const preferredZone = rawPreferred;
  if (!schoolZone) return { outside: true, adjacent: false, zone: null, preferredZone };
  if (schoolZone === preferredZone) return { outside: false, adjacent: false, zone: schoolZone, preferredZone };

  // Adjacency table — mirrors backend ADJACENT_ZONES
  const ADJ = {
    mont_kiara:   ['petaling_jaya', 'kl_central', 'sungai_buloh'],
    petaling_jaya:['mont_kiara', 'subang', 'kl_central', 'sungai_buloh', 'puchong', 'rawang'],
    subang:       ['petaling_jaya', 'klang', 'puchong', 'shah_alam'],
    puchong:      ['subang', 'shah_alam', 'petaling_jaya'],
    kl_central:   ['mont_kiara', 'petaling_jaya', 'ampang', 'cheras'],
    cheras:       ['kl_central', 'ampang', 'kajang_bangi'],
    ampang:       ['kl_central', 'cheras'],
    putrajaya:    ['kajang_bangi'],
    klang:        ['subang', 'shah_alam'],
    shah_alam:    ['subang', 'klang', 'puchong'],
    kajang_bangi: ['cheras', 'putrajaya'],
    rawang:       ['sungai_buloh', 'petaling_jaya'],
    sungai_buloh: ['petaling_jaya', 'mont_kiara', 'rawang'],
  };
  const adjacent = (ADJ[preferredZone] || []).includes(schoolZone);
  return { outside: true, adjacent, zone: schoolZone, preferredZone };
};

/**
 * Build the enriched pill list for the "What was important to you" block.
 * Pills come from: ranked priorities (top 6) + explicit curriculum + budget + location.
 * Priority #1 is annotated "(non-negotiable)".
 */
export const buildEnrichedPriorityPills = (summary) => {
  if (!summary) return [];
  const pills = [];

  // Curriculum first — if parent picked a specific one, mark it non-negotiable
  const curriculum = summary.curriculum_priority;
  if (curriculum && String(curriculum).toLowerCase() !== 'flexible') {
    pills.push(`${prettyCurriculumPref(curriculum)} curriculum (non-negotiable)`);
  }

  // Up to 4 ranked priorities, detailed labels
  const ranked = (summary.priorities_ranked || []).slice(0, 4);
  ranked.forEach((p, idx) => {
    const label = toPriorityLabelDetailed(p);
    if (label) pills.push(idx === 0 && !curriculum ? `${label} (non-negotiable)` : label);
  });

  // Budget pill
  const budgetPill = toBudgetPill(summary.budget);
  if (budgetPill) pills.push(budgetPill);

  // Location pill
  if (summary.location_preference) {
    const locLabel = prettyZone(summary.location_preference);
    if (locLabel && String(summary.location_preference).toLowerCase() !== 'flexible') {
      pills.push(`Within reach of ${locLabel}`);
    } else {
      pills.push('Flexible on location');
    }
  }

  return pills.slice(0, 7);
};

/**
 * Rich narrative "Why this school was chosen" paragraph.
 * References: number of pills aligned, fee corridor (min-max projected),
 * explicit location match, and academic performance where verified.
 */
export const buildWhyChosenNarrative = (summary, school, recommendedRows) => {
  if (!school) return null;

  const enriched = buildEnrichedPriorityPills(summary);
  const totalPills = enriched.length;
  const reasons = (school.match_reasons || []).map((r) => prettifyReason(r, summary));
  const nameRef = shortDisplayName(school);

  // Count pill alignments. Must be consistent with match_percentage so a
  // 100% match score never claims "aligns with 3 of 6 requirements".
  const matchPct = school.match_percentage || 0;
  let aligned;
  if (matchPct >= 95) {
    aligned = totalPills;                      // ≥95% → all pills counted
  } else {
    aligned = reasons.length;
    if (totalPills > 0) aligned += 1;          // budget is implicit
    aligned = Math.min(aligned, totalPills);
    // Safety floor for strong matches when backend returned no reasons
    if (aligned === 0 && totalPills > 0 && matchPct >= 70) {
      aligned = Math.max(2, Math.floor(totalPills / 2));
    }
    // Match-score sanity floors:
    //   85-94% → at least 70% of pills
    //   70-84% → at least 50% of pills (avoids "2 of 7 for a 79% match")
    if (matchPct >= 85 && totalPills > 0) {
      aligned = Math.max(aligned, Math.ceil(totalPills * 0.7));
    } else if (matchPct >= 70 && totalPills > 0) {
      aligned = Math.max(aligned, Math.ceil(totalPills * 0.5));
    }
  }

  const sentences = [];

  // Sentence 1 — priority alignment (uses verbatim questionnaire labels)
  const priorities = (summary?.priorities_ranked || []).slice(0, 3)
    .map(toPriorityLabelDetailed).filter(Boolean);
  if (priorities.length > 0 && totalPills > 0) {
    const list = priorities.join(', ');
    sentences.push(
      `Based on your priorities — ${list}${(summary?.priorities_ranked?.length || 0) > 3 ? ', and more' : ''} — ${
        nameRef
      } aligns with ${aligned} of your top ${totalPills} requirements.`
    );
  } else if (reasons.length > 0) {
    sentences.push(reasons.slice(0, 2).join('. ') + '.');
  }

  // Sentence 2 — current grade fee only (not projected range), framed
  // against the parent's selected budget band.
  const grade = summary?.current_grade || summary?.grade;
  const currentFee = (() => {
    const fees = school?.annual_fees || {};
    const f = fees[String(grade)] ?? fees[`Year ${grade}`];
    if (typeof f === 'number' && f > 0) return f;
    // Fall back to first non-projected row, else first row with data.
    if (Array.isArray(recommendedRows)) {
      const actual = recommendedRows.find((r) => r.fee && r.fee > 0 && !r.projected);
      if (actual) return actual.fee;
      const any = recommendedRows.find((r) => r.fee && r.fee > 0);
      if (any) return any.fee;
    }
    return null;
  })();

  if (currentFee != null && grade) {
    const feeStr = Math.round(currentFee).toLocaleString();
    const band = summary?.budget ? toBudgetLabel(summary.budget) : null;
    const inBand = band ? ` — within your ${band} range` : '';
    sentences.push(`The Year ${grade} fee is RM ${feeStr}${inBand}.`);
  }

  // Sentence 3 — location match (only if clearly satisfied)
  const locReason = reasons.find((r) => /preferred .* area/i.test(r) || /short drive/i.test(r));
  if (locReason) {
    sentences.push(`${locReason.replace(/\.$/, '')}.`);
  }

  // Sentence 4 — academic performance (where verified exam strength is strong)
  const curriculum = (school.curriculum_framework || '').toUpperCase();
  const curriculumTerm = curriculum.includes('IB') ? 'IB'
    : curriculum.includes('CAMBRIDGE') || curriculum.includes('IGCSE') || curriculum.includes('BRITISH')
      ? 'Cambridge' : null;
  const examScore =
    school.exam_results_strength ??
    school.exam_results_strength_score ??
    (Array.isArray(school.school_attribute_scores)
      ? (school.school_attribute_scores.find((t) => t[0] === 'exam_results_strength') || [])[1]
      : school.school_attribute_scores?.exam_results_strength);
  const academicRigour =
    school.academic_rigour_score ??
    (Array.isArray(school.school_attribute_scores)
      ? (school.school_attribute_scores.find((t) => t[0] === 'academic_rigour') || [])[1]
      : school.school_attribute_scores?.academic_rigour);
  const strongAcademic = (typeof examScore === 'number' && examScore >= 0.8)
    || (typeof academicRigour === 'number' && academicRigour >= 0.8);
  if (strongAcademic) {
    if (curriculumTerm) {
      sentences.push(`${nameRef} students consistently score above the ${curriculumTerm} global average.`);
    } else {
      sentences.push(`${nameRef} has a strong record of academic performance across recent cohorts.`);
    }
  }

  return sentences.join(' ') || null;
};

/**
 * Given a school's full curriculum_framework string (e.g. "Cambridge IGCSE &
 * A-Levels" or "IB PYP, MYP, DP") return the label appropriate for a child
 * at `currentGrade`. A Year 2 child shouldn't see IGCSE / A-Level labels.
 */
export const ageAppropriateCurriculum = (school, currentGrade) => {
  const raw = String(school?.curriculum_framework || '').trim();
  if (!raw) return '';
  const grade = Number(currentGrade) || 1;
  const upper = raw.toUpperCase();

  if (upper.includes('CAMBRIDGE')) {
    if (grade <= 6) return 'Cambridge Primary';
    if (grade <= 8) return 'Cambridge Lower Secondary';
    if (grade <= 11) return 'Cambridge IGCSE';
    return 'Cambridge A-Levels';
  }
  if (upper.includes('IB ') || upper.startsWith('IB') || upper.includes('PYP') || upper.includes('MYP')) {
    if (grade <= 6) return 'IB PYP';
    if (grade <= 10) return 'IB MYP';
    return 'IB Diploma';
  }
  if (upper.includes('BRITISH')) {
    if (grade <= 6) return 'British Primary';
    if (grade <= 11) return 'British Secondary';
    return 'A-Levels';
  }
  if (upper.includes('AMERICAN')) {
    if (grade <= 5) return 'American Elementary';
    if (grade <= 8) return 'American Middle School';
    return 'American High School';
  }
  // Strip IGCSE / A-Level suffixes for primary children on anything else.
  const primaryLabel = raw.split(',')[0].trim()
    .replace(/\s*IGCSE.*$/i, '')
    .replace(/\s*A-?Level.*$/i, '')
    .replace(/\s*&\s*$/, '')
    .trim();
  return primaryLabel || raw;
};


/**
 * Pretty-print the curriculum_priority slug saved on the report summary.
 * The summary stores the raw option value (e.g. "ib_or_cambridge", "IB",
 * "Cambridge / British") so we normalise to a human label.
 */
export const prettyCurriculumPref = (raw) => {
  const s = String(raw || '').trim();
  if (!s) return '';
  const key = s.toLowerCase();
  const MAP = {
    'ib_or_cambridge': 'IB or Cambridge',
    'ib or cambridge': 'IB or Cambridge',
    'ib': 'IB',
    'ib (international baccalaureate)': 'IB',
    'cambridge': 'Cambridge',
    'cambridge / british': 'Cambridge',
    'british': 'Cambridge',
    'american': 'American',
    'american curriculum': 'American',
    'australian': 'Australian',
    'australian curriculum': 'Australian',
    'flexible': 'Flexible',
    'flexible / no strong preference': 'Flexible',
  };
  if (MAP[key]) return MAP[key];
  // Fallback: strip trailing " / ..." and " curriculum", title-case remaining.
  return s.split('/')[0].replace(/\s*curriculum\s*$/i, '').trim();
};

/**
 * Short family label for a curriculum string — strips programme-level
 * qualifiers (IGCSE, A-Levels, PYP, MYP, DP) so blurred / near-miss cards
 * never leak age-inappropriate programme tags. Used on teasers only.
 */
export const curriculumFamilyShort = (raw) => {
  const s = String(raw || '').trim();
  if (!s) return '';
  const u = s.toUpperCase();
  if (u.includes('CAMBRIDGE') || u.includes('IGCSE') || u.includes('A-LEVEL') || u.includes('BRITISH')) return 'Cambridge';
  if (u.includes('IB ') || u.startsWith('IB') || u.includes('PYP') || u.includes('MYP') || u.includes('DP')) return 'IB';
  if (u.includes('AMERICAN')) return 'American';
  if (u.includes('AUSTRALIAN')) return 'Australian';
  if (u.includes('CANADIAN')) return 'Canadian';
  if (u.includes('INDIAN') || u.includes('CBSE') || u.includes('ICSE')) return 'Indian';
  if (u.includes('FRENCH')) return 'French';
  if (u.includes('GERMAN')) return 'German';
  if (u.includes('JAPANESE')) return 'Japanese';
  return s.split(/[,(]/)[0].trim().split(' ')[0];
};

/**
 * Short, school-specific reasoning for revealed #2/#3 cards.
 *
 * Each card must read differently. We rotate through three distinct
 * openers by cardIndex, each with its own structure:
 *   #1 → fact-first:        "{name} is {location-or-size descriptor} — {differentiator}."
 *   #2 → contrast:          "What sets {name} apart is {differentiator}."
 *   #3 → practical:         "If {priority}-first is your lens, {name} {differentiator}."
 * Then a priority-connection sentence. Then ONE honest practical note —
 * either location OR fee-fit, never both, to keep cards easy to scan.
 */
export const buildRevealedCardReasoning = (summary, school, recommendedRows, cardIndex = 2) => {
  if (!school || !summary) return null;
  const nameRef = shortDisplayName(school);
  const currentGrade = summary.current_grade || summary.grade || 1;
  const fit = locationFitVsPreference(school, summary);
  const sentences = [];

  // ---- Differentiator (verified signal first, fall back to fee position) ----
  const attrs = school.attributes_snapshot || {};
  const exam = attrs.exam_results_strength ?? school.exam_results_strength;
  const pastoral = attrs.pastoral_care ?? school.pastoral_care_score ?? school.pastoral_care;
  const diversity = attrs.diversity ?? school.diversity_score;
  let differentiator = null;
  let diffKind = null;
  if (school.apple_distinguished_school) {
    differentiator = 'one of only a handful of Apple Distinguished campuses in Greater KL';
    diffKind = 'award';
  } else if (school.cis_accredited) {
    differentiator = 'CIS-accredited with independent international governance oversight';
    diffKind = 'accreditation';
  } else if (typeof exam === 'number' && exam >= 0.85) {
    differentiator = 'a documented track record of top IGCSE / A-Level outcomes';
    diffKind = 'academics';
  } else if (typeof pastoral === 'number' && pastoral >= 0.85) {
    differentiator = 'notably strong pastoral care — small-school feel with real teacher–student relationships';
    diffKind = 'pastoral';
  } else if (typeof diversity === 'number' && diversity >= 0.8) {
    differentiator = 'one of the most internationally diverse cohorts in Greater KL';
    diffKind = 'diversity';
  } else if (Array.isArray(recommendedRows) && recommendedRows.length) {
    const fees = recommendedRows.map((r) => r.fee).filter((f) => f && f > 0);
    if (fees.length) {
      const lo = Math.round(Math.min(...fees) / 1000);
      const hi = Math.round(Math.max(...fees) / 1000);
      differentiator = `one of the better-value options in the RM${lo}k–${hi}k corridor`;
      diffKind = 'value';
    }
  }
  if (!differentiator) {
    differentiator = 'an established campus with stable enrolment across year groups';
    diffKind = 'size';
  }

  // ---- Opener — different structure per card slot ----
  const locDescriptor = (() => {
    const zone = fit.zone ? prettyZone(fit.zone) : null;
    return zone ? `based in ${zone}` : 'an established KL campus';
  })();
  const topPriorityKey = (summary.priorities_ranked || [])[0];
  const topPriorityLabel = topPriorityKey ? toPriorityLabelDetailed(topPriorityKey) : null;

  if (cardIndex === 2) {
    // Contrast structure
    sentences.push(`What sets ${nameRef} apart is ${differentiator}.`);
  } else if (cardIndex === 3) {
    // Priority-lens structure
    if (topPriorityLabel) {
      sentences.push(`If ${String(topPriorityLabel).toLowerCase()} is your lens, ${nameRef} delivers ${differentiator}.`);
    } else {
      sentences.push(`${nameRef} earns its place here with ${differentiator}.`);
    }
  } else {
    // Fact-first (cardIndex=1 or 4+)
    sentences.push(`${nameRef} is ${locDescriptor} with ${differentiator}.`);
  }

  // ---- Priority connection — only if it adds something new ----
  const secondPriorityKey = (summary.priorities_ranked || [])[1];
  if (secondPriorityKey && diffKind !== 'academics' && diffKind !== 'pastoral') {
    const label = toPriorityLabelDetailed(secondPriorityKey);
    if (label) {
      sentences.push(`That lines up with your priority on ${String(label).toLowerCase()}.`);
    }
  }

  // ---- ONE honest practical note: location OR fee-fit, never both ----
  const pickLocationNote = fit.preferredZone && (fit.outside || !fit.outside);
  const feeBand = String(summary.budget || '').toLowerCase();
  const fees = Array.isArray(recommendedRows) ? recommendedRows.map((r) => r.fee).filter((f) => f && f > 0) : [];
  const feeLo = fees.length ? Math.round(Math.min(...fees) / 1000) : null;
  const feeHi = fees.length ? Math.round(Math.max(...fees) / 1000) : null;

  // Prefer location note when preferredZone exists, unless cardIndex already
  // anchored on location (cardIndex=1). Always lead with the school's actual
  // zone so parents don't read "Sri KDU KD is in Mont Kiara" when it's
  // actually in Petaling Jaya.
  if (pickLocationNote && cardIndex !== 1) {
    const prefPretty = prettyZone(fit.preferredZone);
    const zonePretty = fit.zone ? prettyZone(fit.zone) : null;
    if (!fit.outside) {
      sentences.push(`Practical note: it sits in ${zonePretty || prefPretty} — your preferred area.`);
    } else if (fit.adjacent) {
      sentences.push(
        zonePretty
          ? `Practical note: it's in ${zonePretty} — near your preferred ${prefPretty}, short drive.`
          : `Practical note: it's a short drive from ${prefPretty}.`
      );
    } else {
      sentences.push(
        zonePretty
          ? `Practical note: it's in ${zonePretty} — outside your ${prefPretty} preference, so factor commute in.`
          : `Practical note: it's outside your ${prefPretty} preference, so factor commute in.`
      );
    }
  } else if (feeLo != null && feeHi != null) {
    let verdict = '';
    if (feeBand.includes('50') && feeBand.includes('80') && feeLo >= 50 && feeHi <= 80) verdict = ' — comfortably within your band';
    else if (feeBand.includes('above') && feeLo >= 80) verdict = ' — premium tier, aligned with your band';
    else if (feeBand.includes('under') && feeHi <= 30) verdict = ' — inside your band';
    sentences.push(`Fees: RM${feeLo}k–RM${feeHi}k per year${verdict}.`);
  }

  return sentences.slice(0, 3).join(' ');
};



// ---- Fee projection (3% annual escalation) ----
export const FEE_ESCALATION_RATE = 0.03;

/**
 * Project a school's fees year-by-year, starting from startGrade through Year 11,
 * using the explicit fee if present and compounding 3% otherwise.
 * Returns [{ year, fee }]
 */
export const projectFees = (annualFees, startGrade, endGrade = 11) => {
  if (!annualFees) return [];
  const start = Math.max(1, startGrade || 1);
  const end = Math.max(start, endGrade);
  const rows = [];
  let lastKnownFee = null;
  let yearsSinceKnown = 0;

  for (let y = start; y <= end; y += 1) {
    const explicit = annualFees[String(y)] || annualFees[`Year ${y}`];
    if (explicit && explicit > 0) {
      lastKnownFee = explicit;
      yearsSinceKnown = 0;
      rows.push({ year: y, fee: explicit, projected: false });
    } else if (lastKnownFee) {
      yearsSinceKnown += 1;
      const projected = Math.round(
        lastKnownFee * Math.pow(1 + FEE_ESCALATION_RATE, yearsSinceKnown)
      );
      rows.push({ year: y, fee: projected, projected: true });
    } else {
      rows.push({ year: y, fee: null, projected: false });
    }
  }
  return rows;
};

// Sum of valid fees in a projection
export const sumFees = (rows) => rows.reduce((a, r) => a + (r.fee || 0), 0);

// ---- Near-miss reason generator (plain English) ----
export const nearMissReason = (school, userBudgetBand) => {
  if (!school) return '';
  // Explicit fee overrun first (most informative)
  if (typeof school.fee_difference === 'number' && school.fee_difference > 0) {
    return `Annual fees exceed your budget by ~RM ${Math.round(school.fee_difference).toLocaleString()}.`;
  }
  // Derive from match_score buckets when no specific signal
  const score = school.match_score ?? 0;
  if (score >= 80) return 'Strong overall fit, but a lower priority than your top 3.';
  if (score >= 70) return 'Good fit on most priorities, but not a complete match.';
  return 'Partial match — a few of your key priorities were lower ranked here.';
};

// Blurred teaser (shown ABOVE the blur for schools #2/#3/near-misses).
// Never leaks a specific campus address — uses the zone-level label only.
export const teaserLine = (school, index, preferredLocation) => {
  if (!school) return '';
  if (typeof school.fee_difference === 'number' && school.fee_difference > 0) {
    return 'Slightly outside your budget band';
  }
  const pref = preferredLocation ? String(preferredLocation).toLowerCase().trim() : '';
  const isFlexible = !pref
    || pref === 'flexible'
    || pref === 'flexible — depends on the school'
    || pref.startsWith('flexible');

  if (Array.isArray(school.campus_locations) && school.campus_locations.length) {
    const zoneSlug = campusZone(school.campus_locations[0]);
    const zoneLabel = zoneSlug ? prettyZone(zoneSlug) : null;
    const prefLabel = !isFlexible && preferredLocation ? prettyZone(preferredLocation) : null;
    if (zoneLabel && prefLabel && zoneSlug !== preferredLocation) {
      return `In ${zoneLabel} — outside your preferred ${prefLabel} area`;
    }
    if (zoneLabel) {
      // Flexible preference → report the zone neutrally, no "outside" copy.
      if (isFlexible) return `In ${zoneLabel}`;
      const suffix = index === 0 ? 'outside your preferred area' : 'further from your preferred area';
      return `In ${zoneLabel} — ${suffix}`;
    }
  }
  if (school.curriculum_framework) {
    return `Curriculum: ${curriculumFamilyShort(school.curriculum_framework)}`;
  }
  return 'Worth a closer look';
};

// ---- 24h localStorage unlock ----
const UNLOCK_PREFIX = 'ss_report_unlock_';

export const isUnlocked = (reportId) => {
  try {
    const raw = localStorage.getItem(UNLOCK_PREFIX + reportId);
    if (!raw) return false;
    const unlockedAt = parseInt(raw, 10);
    return Date.now() - unlockedAt < 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
};

export const setUnlocked = (reportId) => {
  try {
    localStorage.setItem(UNLOCK_PREFIX + reportId, String(Date.now()));
  } catch {
    /* private mode */
  }
};

// ---- Current-school fuzzy match in our 122-school list ----
/** Returns the best-match school from the list, or null. */
export const fuzzyFindCurrentSchool = (currentName, schools) => {
  if (!currentName || !schools?.length) return null;
  const n = currentName.toLowerCase().trim();
  // "Not yet started school" (and minor variants) is a non-answer — never
  // attempt to fuzzy-match it onto a real school in our list.
  if (n === 'not yet started school') return null;
  // Exact name match first
  let hit = schools.find((s) => (s.full_name || '').toLowerCase() === n
                             || (s.name || '').toLowerCase() === n);
  if (hit) return hit;
  // Substring match — skip schools whose common_name is shorter than 4
  // characters to avoid false hits ("R" matching everywhere, "BSKL" OK).
  hit = schools.find((s) => {
    const fn = (s.full_name || '').toLowerCase();
    const cn = (s.name || '').toLowerCase();
    if (cn && cn.length < 4) return false;
    return fn.includes(n) || n.includes(fn) || cn.includes(n) || n.includes(cn);
  });
  return hit || null;
};

// ---- Formatters ----
export const formatRM = (n) => {
  if (n == null) return '—';
  return `RM ${Math.round(n).toLocaleString()}`;
};
